import React from 'react';
import {
  X, History, ArrowDownLeft, ArrowUpRight, AlertTriangle,
  ShoppingBag, Package, Trash2, Utensils, ClipboardCheck,
  Calendar, Layers, DollarSign
} from 'lucide-react';
import { useItemKardex } from '../hooks/useInventory';

interface KardexModalProps {
  itemId: number | string | null;
  onClose: () => void;
}

export const KardexModal: React.FC<KardexModalProps> = ({ itemId, onClose }) => {
  const { data, isLoading } = useItemKardex(itemId || undefined);

  if (!itemId) return null;

  const item = data?.item;
  const summary = data?.summary;
  const movements = data?.movements || [];

  const getTagBadge = (tag: string) => {
    switch (tag) {
      case 'Venta':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-500/10 text-purple-600 border border-purple-500/20">
            <ShoppingBag size={12} /> Venta
          </span>
        );
      case 'Compra':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
            <Package size={12} /> Compra
          </span>
        );
      case 'Merma':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-500/10 text-red-600 border border-red-500/20">
            <Trash2 size={12} /> Merma
          </span>
        );
      case 'Producción':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-500/10 text-blue-600 border border-blue-500/20">
            <Utensils size={12} /> Producción
          </span>
        );
      case 'Auditoría Física':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
            <ClipboardCheck size={12} /> Toma Física
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-surface-muted text-muted border border-divider">
            Ajuste
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-3xl bg-surface border border-divider shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-divider bg-surface-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-primary/10 text-primary">
              <History size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-extrabold text-[color:var(--color-text)]">
                  {item ? item.name : 'Kardex de Insumo'}
                </h3>
                {item?.sku && (
                  <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-surface-muted border border-divider text-muted">
                    {item.sku}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted mt-0.5">
                Trazabilidad cronológica completa de entradas, salidas, mermas y auditorías.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* KPIs Summary */}
        {item && summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-surface-muted/20 border-b border-divider text-xs">
            <div className="p-3 rounded-2xl bg-surface border border-divider">
              <span className="text-muted block text-[11px] uppercase tracking-wider font-semibold">Stock Actual</span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className={`text-xl font-extrabold ${item.current_stock <= item.min_stock ? 'text-red-500' : 'text-primary'}`}>
                  {item.current_stock.toLocaleString()}
                </span>
                <span className="text-muted font-medium">{item.unit}</span>
              </div>
            </div>

            <div className="p-3 rounded-2xl bg-surface border border-divider">
              <span className="text-muted block text-[11px] uppercase tracking-wider font-semibold">Valor en Stock</span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-xl font-extrabold text-emerald-600">
                  ${item.stock_value.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="p-3 rounded-2xl bg-surface border border-divider">
              <span className="text-muted block text-[11px] uppercase tracking-wider font-semibold">Total Entradas (+)</span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-xl font-extrabold text-emerald-600">
                  +{summary.total_in.toLocaleString()}
                </span>
                <span className="text-muted font-medium">{item.unit}</span>
              </div>
            </div>

            <div className="p-3 rounded-2xl bg-surface border border-divider">
              <span className="text-muted block text-[11px] uppercase tracking-wider font-semibold">Total Salidas (-)</span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-xl font-extrabold text-red-500">
                  -{summary.total_out.toLocaleString()}
                </span>
                <span className="text-muted font-medium">{item.unit}</span>
              </div>
            </div>
          </div>
        )}

        {/* Content / Timeline */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="py-20 text-center text-muted text-sm flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              Cargando historial de movimientos...
            </div>
          ) : movements.length === 0 ? (
            <div className="py-16 text-center text-muted text-sm space-y-2">
              <History size={36} className="mx-auto text-muted/40" />
              <p>No hay movimientos registrados para este insumo.</p>
            </div>
          ) : (
            <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-divider">
              {movements.map((m) => {
                const isPositive = m.signed_qty >= 0;
                return (
                  <div key={m.id} className="relative group">
                    {/* Timeline bullet */}
                    <div
                      className={`absolute -left-6 top-2 w-5 h-5 rounded-full border-2 border-surface flex items-center justify-center text-[10px] ${
                        isPositive ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                      }`}
                    >
                      {isPositive ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                    </div>

                    <div className="p-4 rounded-2xl bg-surface border border-divider hover:border-primary/40 transition-all shadow-xs space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getTagBadge(m.category_tag)}
                          <span className="text-xs font-bold text-[color:var(--color-text)]">
                            {m.reason || 'Movimiento de inventario'}
                          </span>
                          {m.reference && (
                            <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-surface-muted border border-divider text-muted">
                              Ref: {m.reference}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted flex items-center gap-1">
                          <Calendar size={13} />
                          {m.moved_at ? new Date(m.moved_at).toLocaleString() : 'Fecha no registrada'}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs pt-1 border-t border-divider/60">
                        <div className="flex items-center gap-4 text-muted">
                          <span>
                            Antes: <strong className="text-[color:var(--color-text)]">{m.stock_before} {m.unit}</strong>
                          </span>
                          <span>
                            Movimiento:{' '}
                            <strong className={isPositive ? 'text-emerald-600' : 'text-red-500'}>
                              {isPositive ? '+' : ''}{m.signed_qty} {m.unit}
                            </strong>
                          </span>
                        </div>
                        <div>
                          <span className="text-muted mr-1.5">Saldo Resultante:</span>
                          <strong className="text-sm font-extrabold text-primary">
                            {m.stock_after} {m.unit}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-divider bg-surface-muted/30 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl border border-divider text-xs font-bold hover:bg-surface-muted transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
